import { useEffect, useState } from "react";
import { FencyContentStatus, SignedDocumentDto, ViewDocumentAsOwnerDto } from "../../autogen/bff-api";
import { useFency } from "./useFency";
import { parseFencyContentStatus } from "./parseFencyContentStatus";
import { getPusher } from "../pusher";

export type DocumentSyncStatus = {
  doc: ViewDocumentAsOwnerDto | SignedDocumentDto;
  status: FencyContentStatus | null;
  error: boolean;
};

export const useFencyBulkDocumentSync = (
  documents: (ViewDocumentAsOwnerDto | SignedDocumentDto)[],
  refetchDocuments?: () => void | Promise<void>
) => {
  const { triggerSync } = useFency();
  const [documentStatuses, setDocumentStatuses] = useState<DocumentSyncStatus[]>(() =>
    documents.map((doc) => ({
      doc,
      status: doc.fencyContentStatus ?? null,
      error: false,
    }))
  );

  useEffect(() => {
    setDocumentStatuses((prev) => {
      const byId = new Map(prev.map((p) => [p.doc.id, p]));
      return documents.map((doc) => {
        const existing = byId.get(doc.id);
        return existing ?? { doc, status: doc.fencyContentStatus ?? null, error: false };
      });
    });
  }, [documents]);

  useEffect(() => {
    const bindings: { channel: ReturnType<ReturnType<typeof getPusher>["subscribe"]>; handler: (data: unknown) => void }[] = [];

    documents.forEach((doc) => {
      if (doc.fencyContentStatus === FencyContentStatus.Synchronized) {
        return;
      }

      const runSync = async () => {
        setDocumentStatuses((prev) =>
          prev.map((item) => (item.doc.id === doc.id ? { ...item, error: false } : item))
        );
        try {
          await triggerSync(doc);
          void refetchDocuments?.();
        } catch {
          setDocumentStatuses((prev) =>
            prev.map((item) => (item.doc.id === doc.id ? { ...item, error: true } : item))
          );
        }
      };

      runSync();

      const handler = (data: unknown) => {
        const status = parseFencyContentStatus(data);
        if (status) {
          setDocumentStatuses((prev) =>
            prev.map((item) => (item.doc.id === doc.id ? { ...item, status, error: false } : item))
          );
          void refetchDocuments?.();
        }
      };

      const channel = getPusher().subscribe(doc.id);
      channel.bind("fency-memory-updated", handler);
      bindings.push({ channel, handler });
    });

    return () => {
      bindings.forEach(({ channel, handler }) => {
        channel.unbind("fency-memory-updated", handler);
      });
    };
  }, [documents, triggerSync, refetchDocuments]);

  useEffect(() => {
    setDocumentStatuses((prev) =>
      prev.map((item) => {
        const doc = documents.find((d) => d.id === item.doc.id);
        if (doc?.fencyContentStatus === FencyContentStatus.Synchronized) {
          return { ...item, status: FencyContentStatus.Synchronized };
        }
        return item;
      })
    );
  }, [documents]);

  return { documentStatuses };
};
